import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AI_CONVERSATION_MISMATCH_MESSAGE,
  AI_CONVERSATION_TTL_SECONDS,
  AI_LIMIT_MESSAGE,
  AI_UNAVAILABLE_MESSAGE,
  readSseStream,
} from '@repo/lib';
import { useAuth } from '../AuthContext/AuthContext';
import { usePageSellerId } from '../../hooks/usePage';
import { apiUrl } from '../../services/env';
import storage from '../../services/storage';
import { checkAuth, fetchWithNetworkErrorHandling } from '../../services/request';
import useMountEffect from '../../hooks/useMountEffect';
import { fetchAIChatHistory } from '../../hooks/queries/useAIQueries';
import { aiChatHistoryKey, pageKey, sellerProductsKey, themesKey } from '../../lib/queryKeys';
import { isAIEditorPath } from './aiEditorRoute';
import { answerCapture } from './previewCapture';

const AIChatsContext = createContext(null);
const AIStateContext = createContext();
const AIDispatchContext = createContext();

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Try again.';
const CONNECTION_LOST_MESSAGE = 'Connection lost. Try again.';

const NO_ITEMS = [];
const NO_CHATS = {};
const MAX_CHATS = 10;

// How long the preview outlines a turn's changes after the latest one, and a change after its Show.
export const AI_CHANGE_RING_MS = 8_000;

let turnCount = 0;
const nextTurnId = () => `turn-${++turnCount}`;

let focusCount = 0;

const initialState = {
  turns: [],
  isStreaming: false,
  responseId: null,
  conversationId: null,
  lastSentAt: null,
  // Until the first message or New chat, the page's saved conversation can still fill an empty chat.
  canRestore: true,
  mode: 'edit',
  guide: null,
  draft: '',
};

function errorKindOf(message) {
  if (message === AI_LIMIT_MESSAGE) return 'limit';
  if (message === AI_UNAVAILABLE_MESSAGE) return 'unavailable';
  return 'other';
}

function httpErrorOf(status, body) {
  const message = body?.message || GENERIC_ERROR_MESSAGE;
  const kind =
    status === 400 && message === AI_CONVERSATION_MISMATCH_MESSAGE
      ? 'mismatch'
      : status === 503
        ? 'unavailable'
        : status === 429
          ? 'limit'
          : 'other';
  return { kind, message };
}

// A tool call that throws on the server never sends its end event, so a finished turn closes it.
function closeSteps(turn, status) {
  return {
    ...turn,
    steps: turn.steps.map(step => (step.status === 'running' ? { ...step, status } : step)),
  };
}

// Stop and a dropped connection don't abort the server's calls, so a write still running may land.
function failTurn(turn, error, stepStatus = 'failed') {
  return { ...closeSteps(turn, stepStatus), status: 'error', error };
}

function applyTool(steps, { id, name, phase, ok, step: text }) {
  const withText = text ? { text } : {};
  if (phase === 'start') return [...steps, { id, name, ...withText, status: 'running' }];

  const status = ok ? 'done' : 'failed';
  if (!steps.some(step => step.id === id)) return [...steps, { id, name, ...withText, status }];
  return steps.map(step => (step.id === id ? { ...step, ...withText, status } : step));
}

function applyEvent(turn, event, data, at) {
  if (turn.status === 'stopped') return turn;

  switch (event) {
    case 'text':
      return { ...turn, text: turn.text + (data?.content ?? '') };
    case 'tool':
      return { ...turn, steps: applyTool(turn.steps, data ?? {}) };
    case 'applied':
      return data?.change
        ? { ...turn, changes: [...turn.changes, data.change], lastChangeAt: at }
        : turn;
    case 'point':
      return data ? { ...turn, points: [...turn.points, data] } : turn;
    case 'upload':
      return data ? { ...turn, uploads: [...turn.uploads, data] } : turn;
    case 'error': {
      const message = data?.message || GENERIC_ERROR_MESSAGE;
      return failTurn(turn, { kind: errorKindOf(message), message });
    }
    case 'done':
      return {
        ...closeSteps(turn, 'done'),
        status: turn.status === 'streaming' ? 'done' : turn.status,
      };
    default:
      return turn;
  }
}

// A saved turn left a call unfinished the way the live stream closes it for that outcome.
const UNFINISHED_STEP_STATUS = { done: 'done', error: 'failed', stopped: 'stopped' };

function restoreTurn(saved) {
  const unfinished = UNFINISHED_STEP_STATUS[saved.status];
  return [
    {
      id: `${saved.id}:user`,
      role: 'user',
      content: saved.message,
      ...(saved.attachments?.length > 0 && { attachments: saved.attachments }),
      mode: saved.mode,
      guide: saved.guide ?? null,
    },
    {
      id: `${saved.id}:assistant`,
      role: 'assistant',
      mode: saved.mode,
      status: saved.status,
      text: saved.reply,
      steps: saved.tools.map(({ id, name, step, ok }) => ({
        id,
        name,
        ...(step && { text: step }),
        status: ok === undefined ? unfinished : ok ? 'done' : 'failed',
      })),
      changes: saved.changes,
      points: saved.points,
      uploads: saved.uploads ?? [],
      error:
        saved.status === 'error'
          ? {
              kind: errorKindOf(saved.error),
              message: saved.error || GENERIC_ERROR_MESSAGE,
            }
          : null,
      restored: true,
    },
  ];
}

function lastSavedResponseId(savedTurns) {
  for (let i = savedTurns.length - 1; i >= 0; i--) {
    if (savedTurns[i].responseId) return savedTurns[i].responseId;
  }
  return null;
}

const restoreTurns = history => history.turns.flatMap(restoreTurn);

function restoredFields(history) {
  const saved = history.turns;
  return {
    conversationId: history.conversationId,
    responseId: lastSavedResponseId(saved),
    lastSentAt: Date.parse(saved[saved.length - 1].createdAt),
    canRestore: false,
  };
}

const clearMarkKey = (userId, pageId) => `aiChatCleared:${userId}:${pageId}`;

function readClearMark(userId, pageId) {
  try {
    const mark = JSON.parse(storage.local.getItem(clearMarkKey(userId, pageId)) ?? 'null');
    return typeof mark?.at === 'number' ? mark : null;
  } catch {
    return null;
  }
}

function writeClearMark(userId, pageId, mark) {
  try {
    storage.local.setItem(clearMarkKey(userId, pageId), JSON.stringify(mark));
  } catch {
    // Without storage, New chat still clears this visit; a reload may bring the old chat back.
  }
}

// New chat keeps the cleared conversation hidden after a reload. Its id is compared when known, since the
// browser's clock can disagree with the server's; the time covers a clear made before the history loaded.
function historyAfterClear(history, mark) {
  if (!history || !mark) return history;
  const turns = history.turns.filter(turn =>
    mark.conversationId
      ? turn.conversationId !== mark.conversationId
      : Date.parse(turn.createdAt) > mark.at,
  );
  return turns.length === history.turns.length ? history : { ...history, turns };
}

function updateTurn(turns, turnId, update) {
  return turns.map(turn => (turn.id === turnId ? update(turn) : turn));
}

function updateStreamingTurn(turns, turnId, update) {
  return updateTurn(turns, turnId, turn => (turn.status === 'streaming' ? update(turn) : turn));
}

// The open guide's card is the only one, always last.
const withoutGuideCard = turns =>
  turns[turns.length - 1]?.role === 'guide' ? turns.slice(0, -1) : turns;

export function turnsReducer(turns, action) {
  switch (action.type) {
    case 'SEND': {
      const sent = [
        {
          id: action.userTurnId,
          role: 'user',
          content: action.content,
          ...(action.attachments?.length > 0 && { attachments: action.attachments }),
          mode: action.mode,
          guide: action.guide,
          ...(action.keepLook && { keepLook: true }),
        },
        {
          id: action.turnId,
          role: 'assistant',
          mode: action.mode,
          status: 'streaming',
          text: '',
          steps: [],
          changes: [],
          points: [],
          uploads: [],
          error: null,
        },
      ];
      // The guide card only helps write the message; the guide stays attached in the message box.
      const kept = withoutGuideCard(turns);
      return action.newConversation
        ? [...kept, { id: `${action.userTurnId}:divider`, role: 'divider' }, ...sent]
        : [...kept, ...sent];
    }
    case 'EVENT':
      return updateTurn(turns, action.turnId, turn =>
        applyEvent(turn, action.event, action.data, action.at),
      );
    case 'HTTP_ERROR':
      return updateStreamingTurn(turns, action.turnId, turn => failTurn(turn, action.error));
    case 'STOP':
      return updateStreamingTurn(turns, action.turnId, turn => ({
        ...closeSteps(turn, 'stopped'),
        status: 'stopped',
      }));
    case 'END':
      return updateStreamingTurn(turns, action.turnId, turn =>
        failTurn(turn, { kind: 'other', message: CONNECTION_LOST_MESSAGE }, 'stopped'),
      );
    case 'SET_GUIDE': {
      const kept = withoutGuideCard(turns);
      if (!action.payload) return kept;
      const card = { id: action.turnId, role: 'guide', guide: action.payload };
      return [...kept, action.from ? { ...card, from: action.from } : card];
    }
    default:
      return turns;
  }
}

export function aiReducer(state, action) {
  switch (action.type) {
    case 'HYDRATE':
      if (!state.canRestore || action.history.turns.length === 0) return state;
      return {
        ...state,
        ...restoredFields(action.history),
        // A guide card opened while the history loaded stays below it.
        turns: [...restoreTurns(action.history), ...state.turns],
      };
    case 'SEND':
      return {
        ...state,
        turns: turnsReducer(state.turns, action),
        isStreaming: true,
        conversationId: action.conversationId ?? null,
        responseId: action.newConversation ? null : state.responseId,
        lastSentAt: action.sentAt,
        canRestore: false,
      };
    case 'STOP':
    case 'END':
      return { ...state, turns: turnsReducer(state.turns, action), isStreaming: false };
    case 'HTTP_ERROR':
      return { ...state, turns: turnsReducer(state.turns, action) };
    case 'EVENT':
      return {
        ...state,
        turns: turnsReducer(state.turns, action),
        responseId: action.event === 'done' ? (action.data?.responseId ?? null) : state.responseId,
        conversationId:
          action.event === 'done' && action.data?.conversationId
            ? action.data.conversationId
            : state.conversationId,
      };
    case 'NEW_CHAT':
      return {
        ...state,
        turns: [],
        responseId: null,
        conversationId: null,
        lastSentAt: null,
        canRestore: false,
        guide: null,
        draft: '',
        isStreaming: false,
      };
    case 'SET_DRAFT':
      return { ...state, draft: action.payload };
    case 'SET_MODE':
      return { ...state, mode: action.payload };
    case 'SET_GUIDE':
      return { ...state, guide: action.payload, turns: turnsReducer(state.turns, action) };
    default:
      return state;
  }
}

const emptyStore = owner => ({ owner, chats: {}, recent: [] });

// Keeps the chats of the MAX_CHATS most recently used pages, and any chat still streaming.
function chatsReducer(store, action) {
  if (action.type === 'RESET') return emptyStore(action.owner);
  // A reply that lands after a sign-out or store switch belongs to the chats that were cleared.
  if (action.owner !== store.owner) return store;

  const chat = store.chats[action.pageId] ?? initialState;
  const next = aiReducer(chat, action);
  if (next === chat) return store;

  const chats = { ...store.chats, [action.pageId]: next };
  const recent = [action.pageId, ...store.recent.filter(id => id !== action.pageId)].filter(
    (id, index) => index < MAX_CHATS || chats[id].isStreaming,
  );
  return {
    owner: store.owner,
    chats: Object.fromEntries(recent.map(id => [id, chats[id]])),
    recent,
  };
}

function abortStreams(streams) {
  for (const stream of streams.values()) stream.controller.abort();
  streams.clear();
}

// Holds each page's chat above the routes: a chat kept in the page editor would reset when the seller
// opens an overview screen, and leaving would abort a streaming reply.
export function AIChatsProvider({ children }) {
  const { userId, sellerId } = useAuth();
  const owner = `${userId ?? ''}:${sellerId ?? ''}`;
  const [store, dispatch] = useReducer(chatsReducer, owner, emptyStore);
  const streamsRef = useRef(new Map());

  // Someone else signing in on this tab, or a store switch, must not see or continue these chats.
  useEffect(() => {
    if (store.owner === owner) return;
    abortStreams(streamsRef.current);
    dispatch({ type: 'RESET', owner });
  }, [owner, store.owner]);

  useMountEffect(() => () => abortStreams(streamsRef.current));

  const chats = store.owner === owner ? store.chats : NO_CHATS;
  const value = useMemo(
    () => ({ owner, chats, dispatch, streams: streamsRef.current }),
    [owner, chats],
  );
  return <AIChatsContext.Provider value={value}>{children}</AIChatsContext.Provider>;
}

function useAIChats() {
  const context = useContext(AIChatsContext);
  if (!context) throw new Error('AIProvider must be used within AIChatsProvider');
  return context;
}

// Re-renders the provider when the next outline is due to clear. A timer that fires a moment
// early still counts as due, or the same key would never schedule another.
function RingTimer({ at, onDue }) {
  useMountEffect(() => {
    const timer = setTimeout(() => onDue(Math.max(Date.now(), at)), at - Date.now());
    return () => clearTimeout(timer);
  });
  return null;
}

function lastTurnOf(turns, role) {
  for (let i = turns.length - 1; i >= 0; i--) {
    if (turns[i].role === role) return turns[i];
  }
  return null;
}

function chatHeaders(token) {
  const currentSellerId = storage.local.getItem('sellerId');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'x-access-token': token }),
    ...(currentSellerId && { 'X-Current-Seller-Id': currentSellerId }),
  };
}

function postCapture({ sellerId, pageId, token, body }) {
  return fetchWithNetworkErrorHandling(
    `${apiUrl}/sellers/${sellerId}/pages/${pageId}/ai/chat/capture`,
    { method: 'POST', headers: chatHeaders(token), body: JSON.stringify(body) },
  );
}

function postChat({
  sellerId,
  pageId,
  token,
  message,
  attachments,
  previousResponseId,
  conversationId,
  mode,
  guide,
  keepLook,
  signal,
}) {
  return fetchWithNetworkErrorHandling(`${apiUrl}/sellers/${sellerId}/pages/${pageId}/ai/chat`, {
    method: 'POST',
    headers: chatHeaders(token),
    body: JSON.stringify({
      message,
      ...(attachments.length > 0 && { attachments }),
      previousResponseId,
      conversationId,
      mode,
      ...(guide && { guide }),
      ...(keepLook && { keepLook }),
    }),
    signal,
  }).then(checkAuth);
}

export function AIProvider({ children, page }) {
  const { owner, chats, dispatch: dispatchChats, streams } = useAIChats();
  const { userId, sellerId: signedInSellerId, token } = useAuth();
  const sellerId = usePageSellerId();
  const location = useLocation();
  const queryClient = useQueryClient();
  const pageId = page?._id;
  const stored = chats[pageId] ?? initialState;
  const dispatch = useCallback(
    action => dispatchChats({ ...action, pageId, owner }),
    [dispatchChats, pageId, owner],
  );

  const canRestore = Boolean(pageId) && stored.canRestore;
  const historyKey = useMemo(
    () => aiChatHistoryKey(sellerId, pageId, userId),
    [sellerId, pageId, userId],
  );
  const { data: history } = useQuery({
    queryKey: historyKey,
    queryFn: () => fetchAIChatHistory({ sellerId, pageId, token }),
    enabled: canRestore,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const clearMark = useMemo(() => readClearMark(userId, pageId), [userId, pageId]);
  const shownHistory = useMemo(() => historyAfterClear(history, clearMark), [history, clearMark]);
  // The saved conversation shows until the chat is used; sending then keeps it in the store.
  const restoring = canRestore && shownHistory?.turns.length > 0 ? shownHistory : null;
  // Typing re-renders with a new stored chat, and new turns would scroll the panel to the bottom on every key.
  const restoredTurns = useMemo(
    () => (restoring ? restoreTurns(restoring) : NO_ITEMS),
    [restoring],
  );
  const turns = useMemo(
    () => (restoring ? [...restoredTurns, ...stored.turns] : stored.turns),
    [restoring, restoredTurns, stored.turns],
  );
  const state = useMemo(
    () => (restoring ? { ...stored, ...restoredFields(restoring), turns } : stored),
    [stored, restoring, turns],
  );

  // Kept out of the stored chat, and tied to the visit it was made in, so a Show never replays.
  const [shown, setShown] = useState(null);
  const shownHere = shown && shown.location === location ? shown : null;
  const focus = shownHere?.focus ?? null;
  const [now, setNow] = useState(() => Date.now());

  const stop = useCallback(() => {
    const stream = streams.get(pageId);
    if (!stream) return;
    streams.delete(pageId);
    stream.controller.abort();
    dispatch({ type: 'STOP', turnId: stream.turnId });
  }, [streams, pageId, dispatch]);

  const sendMessage = useCallback(
    async (message, options = {}) => {
      const attachments = options.attachments ?? [];
      if (!message.trim() && attachments.length === 0) return;

      stop();
      setShown(null);
      const mode = options.mode ?? state.mode;
      const guide = options.guide !== undefined ? options.guide : state.guide;
      const userTurnId = nextTurnId();
      const turnId = nextTurnId();
      const sentAt = Date.now();
      const isExpired =
        state.lastSentAt != null && sentAt - state.lastSentAt > AI_CONVERSATION_TTL_SECONDS * 1000;
      // Safari before 15.4 has no randomUUID; the API then starts the conversation and names it in done.
      const conversationId = (!isExpired && state.conversationId) || crypto.randomUUID?.();
      const previousResponseId = isExpired ? undefined : (state.responseId ?? undefined);
      const controller = new AbortController();
      streams.set(pageId, { controller, turnId });

      if (restoring) dispatch({ type: 'HYDRATE', history: restoring });
      dispatch({
        type: 'SEND',
        userTurnId,
        turnId,
        content: message,
        attachments,
        mode,
        guide: guide ?? null,
        keepLook: options.keepLook,
        conversationId,
        sentAt,
        newConversation: isExpired,
      });
      // The store holds this chat from now on, so a later restore must fetch the conversation afresh.
      queryClient.removeQueries({ queryKey: historyKey, exact: true });

      try {
        const response = await postChat({
          sellerId,
          pageId,
          token,
          message,
          attachments,
          previousResponseId,
          conversationId,
          mode,
          guide,
          keepLook: options.keepLook,
          signal: controller.signal,
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          dispatch({ type: 'HTTP_ERROR', turnId, error: httpErrorOf(response.status, body) });
          return;
        }

        await readSseStream(response.body, (event, data) => {
          if (controller.signal.aborted) return;
          if (event === 'capture') {
            void answerCapture({
              requestId: data?.requestId,
              device: data?.device,
              pageId,
              sellerId: signedInSellerId,
              queryClient,
              post: body => postCapture({ sellerId, pageId, token, body }),
            });
            return;
          }
          if (event === 'applied') {
            // Also refreshes the page's order bumps, which the editor list and the preview read
            // under the page key.
            queryClient.invalidateQueries({ queryKey: pageKey(pageId) });
            // usePage reads the page's theme from the themes list, so a theme the AI changed in
            // place only shows once that list refetches.
            queryClient.invalidateQueries({ queryKey: themesKey(signedInSellerId) });
            // The order bump picker lists the seller's bump products by title.
            queryClient.invalidateQueries({ queryKey: sellerProductsKey(sellerId) });
          }
          dispatch({ type: 'EVENT', turnId, event, data, at: Date.now() });
        });
      } catch {
        // A stop() already marked the turn; anything else is a dropped connection, which END reports.
      } finally {
        if (streams.get(pageId)?.controller === controller) {
          streams.delete(pageId);
          dispatch({ type: 'END', turnId });
        }
      }
    },
    [
      sellerId,
      signedInSellerId,
      pageId,
      token,
      state.responseId,
      state.conversationId,
      state.lastSentAt,
      state.mode,
      state.guide,
      restoring,
      historyKey,
      queryClient,
      stop,
      streams,
      dispatch,
    ],
  );

  const retry = useCallback(() => {
    const lastUserTurn = lastTurnOf(state.turns, 'user');
    if (!lastUserTurn) return undefined;
    return sendMessage(lastUserTurn.content, {
      attachments: lastUserTurn.attachments,
      mode: lastUserTurn.mode,
      guide: lastUserTurn.guide,
      keepLook: lastUserTurn.keepLook,
    });
  }, [state.turns, sendMessage]);

  const newChat = useCallback(() => {
    stop();
    setShown(null);
    writeClearMark(userId, pageId, { at: Date.now(), conversationId: state.conversationId });
    dispatch({ type: 'NEW_CHAT' });
    queryClient.removeQueries({ queryKey: historyKey, exact: true });
  }, [stop, userId, pageId, state.conversationId, dispatch, queryClient, historyKey]);

  const setDraft = useCallback(
    draft => {
      dispatch({ type: 'SET_DRAFT', payload: draft });
    },
    [dispatch],
  );

  const setMode = useCallback(
    mode => {
      dispatch({ type: 'SET_MODE', payload: mode });
    },
    [dispatch],
  );

  const setGuide = useCallback(
    (guide, { from } = {}) => {
      dispatch({ type: 'SET_GUIDE', payload: guide ?? null, turnId: nextTurnId(), from });
    },
    [dispatch],
  );

  // A change's Show outlines it again once the turn's outlines have cleared; a point keeps its number.
  const showInPreview = useCallback(
    ({ target, fieldId, step, op }) => {
      setShown({
        location,
        focus: { target, fieldId, step, nonce: ++focusCount },
        change: op ? { target, fieldId, op } : null,
        until: Date.now() + AI_CHANGE_RING_MS,
      });
    },
    [location],
  );

  const latestTurn = lastTurnOf(state.turns, 'assistant');
  // A restored turn's changes were outlined when they were made; only a turn from this visit draws.
  const liveTurn = latestTurn?.restored ? null : latestTurn;
  const isAhead = until => until !== null && until > now;
  const changesUntil =
    liveTurn?.lastChangeAt != null ? liveTurn.lastChangeAt + AI_CHANGE_RING_MS : null;
  const shownUntil = shownHere?.change ? shownHere.until : null;
  const nextExpiry = Math.min(...[changesUntil, shownUntil].filter(isAhead));

  const value = {
    ...state,
    focus,
    shownChange: isAhead(shownUntil) ? shownHere.change : null,
    latestChanges: isAhead(changesUntil) ? liveTurn.changes : NO_ITEMS,
    latestPoints: liveTurn?.points ?? NO_ITEMS,
    sendMessage,
    stop,
    newChat,
    retry,
    setMode,
    setGuide,
    setDraft,
    showInPreview,
    isOpen: isAIEditorPath(location.pathname),
  };

  return (
    <AIStateContext.Provider value={state}>
      <AIDispatchContext.Provider value={value}>
        {children}
        {nextExpiry !== Infinity && <RingTimer key={nextExpiry} at={nextExpiry} onDue={setNow} />}
      </AIDispatchContext.Provider>
    </AIStateContext.Provider>
  );
}

export function useAIState() {
  const context = useContext(AIStateContext);
  if (context === undefined) {
    throw new Error('useAIState must be used within AIProvider');
  }
  return context;
}

export function useAIDispatch() {
  const context = useContext(AIDispatchContext);
  if (context === undefined) {
    throw new Error('useAIDispatch must be used within AIProvider');
  }
  return context;
}

export function useAI() {
  return useAIDispatch();
}
